import React, { useState, useCallback, useContext, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  Box,
  Text,
  VStack,
  Button,
  Input,
  Textarea,
  Flex,
  Icon,
  useToast,
  List,
  ListItem,
  Image,
  FormControl,
  FormLabel,
} from '@chakra-ui/react';
import { useDropzone } from 'react-dropzone';
import { FaUpload, FaCheckCircle } from 'react-icons/fa';
import debounce from 'lodash/debounce';
import { brandApi } from '../../../services/api/brand';
import { contentPortfolioApi } from '../../../services/api/contentPortfolio';
import { BrandSearchResultDto } from '@foodfluence/shared';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { uploadWithProgress } from '../../../utils/contentUploadUtils';
import ReviewContentModalConfirmation from '../../collabs/components/ReviewContentModalConfirmation';
import ModalShell from '../../../components/ui/ModalShell';
import { ff } from '../../../theme';

interface UploadContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /**
   * `contentPortfolio` (default): UGC flow — preview/confirm step, `contentPortfolioApi` + signed URL.
   * `marketplaceTop`: parent uploads (e.g. Local Creators top slots) via `onMarketplaceTopUpload`, no preview step.
   */
  uploadMode?: 'contentPortfolio' | 'marketplaceTop';
  /** Required when `uploadMode` is `marketplaceTop`. */
  onMarketplaceTopUpload?: (file: File, brandName: string, caption: string) => Promise<void>;
  /** Called when editing an existing slot without replacing the file (brand/caption only). */
  onMarketplaceTopMetaUpdate?: (brandName: string, caption: string) => Promise<void>;
  /** Pre-fill brand when editing an existing slot. */
  initialBrand?: string;
  /** Pre-fill caption when editing an existing slot. */
  initialCaption?: string;
  /** Existing media URL to preview when editing a filled slot. */
  initialContentUrl?: string;
  /** Whether the existing content is a video. */
  initialIsVideo?: boolean;
  /** Sheet header, e.g. "Add top content" for marketplace. */
  title?: string;
}

type ModalState =
  | { type: 'input' }
  | { type: 'preview'; file: File; contentUrl: string; brand: string }
  | { type: 'uploading'; progress: number };

const UploadContentModal: React.FC<UploadContentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  uploadMode = 'contentPortfolio',
  onMarketplaceTopUpload,
  onMarketplaceTopMetaUpdate,
  initialBrand,
  initialCaption,
  initialContentUrl,
  initialIsVideo,
  title: titleText = 'Upload file',
}) => {
  const toast = useToast();
  const { influencerData } = useContext(InfluencerDataContext);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [brandName, setBrandName] = useState('');
  const [caption, setCaption] = useState('');
  const [suggestions, setSuggestions] = useState<BrandSearchResultDto[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<BrandSearchResultDto | null>(null);
  const [isBrandSelected, setIsBrandSelected] = useState(false);
  const [modalState, setModalState] = useState<ModalState>({ type: 'input' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newFilePreviewUrl, setNewFilePreviewUrl] = useState<string | null>(null);

  const isEditingExisting = Boolean(initialContentUrl) && uploadMode === 'marketplaceTop';
  const resolvedBrandName = (selectedBrand?.name ?? brandName).trim();

  // Keep blob preview URL in sync with the selected file (marketplaceTop inline preview).
  useEffect(() => {
    if (!uploadedFile) {
      setNewFilePreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      return;
    }
    const url = URL.createObjectURL(uploadedFile);
    setNewFilePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [uploadedFile]);

  // Seed fields and reset file state when the modal opens (marketplaceTop editing).
  useEffect(() => {
    if (isOpen) {
      setBrandName(initialBrand ?? '');
      setCaption(initialCaption ?? '');
      setIsBrandSelected(Boolean(initialBrand));
      setUploadedFile(null);
    }
  }, [isOpen, initialBrand, initialCaption]);

  const fetchBrandSuggestions = async (input: string) => {
    if (input.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const results = await brandApi.searchBrands(input);
      setSuggestions(results);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error fetching brand suggestions:', error);
      setSuggestions([]);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchBrands = useCallback(debounce(fetchBrandSuggestions, 300), []);

  const handleBrandNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setBrandName(value);
    setIsBrandSelected(false);
    setSelectedBrand(null);
    debouncedFetchBrands(value);
  };

  const handleSuggestionClick = (brand: BrandSearchResultDto) => {
    setSelectedBrand(brand);
    setBrandName(brand.name);
    setIsBrandSelected(true);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleClose = () => {
    setUploadedFile(null);
    setBrandName('');
    setCaption('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedBrand(null);
    setIsBrandSelected(false);
    setModalState({ type: 'input' });
    setIsSubmitting(false); // Reset loading state
    onClose();
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setUploadedFile(acceptedFiles[0]);
    }
  };

  const ACCEPTED_VIDEO_TYPES = {
    'video/mp4': ['.mp4'],
    'video/quicktime': ['.mov'],
    'video/webm': ['.webm'],
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      ...ACCEPTED_VIDEO_TYPES,
      'image/*': [],
    },
    maxFiles: 1,
    multiple: false,
  });

  const handleMarketplaceUpload = async (file: File, brand: string, captionText: string) => {
    if (!onMarketplaceTopUpload) return;
    try {
      await onMarketplaceTopUpload(file, brand, captionText);
      toast({
        title: 'Content saved',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Marketplace top upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error saving your file. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const handleMarketplaceMetaUpdate = async (brand: string, captionText: string) => {
    if (!onMarketplaceTopMetaUpdate) return;
    try {
      await onMarketplaceTopMetaUpdate(brand, captionText);
      toast({
        title: 'Content saved',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onSuccess?.();
      handleClose();
    } catch {
      toast({
        title: 'Save failed',
        description: 'There was an error saving your changes. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (uploadMode === 'marketplaceTop') {
      if (!uploadedFile && !isEditingExisting) {
        toast({
          title: 'Missing information',
          description: 'Please upload a file',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      if (resolvedBrandName.length < 2) {
        toast({
          title: 'Missing information',
          description: 'Please enter a brand name (at least 2 characters) or pick one from suggestions',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const trimmedCaption = caption.trim();
      setIsSubmitting(true);

      if (!uploadedFile && isEditingExisting) {
        await handleMarketplaceMetaUpdate(resolvedBrandName, trimmedCaption);
        return;
      }

      await handleMarketplaceUpload(uploadedFile!, resolvedBrandName, trimmedCaption);
      return;
    }

    if (!uploadedFile || !isBrandSelected || !selectedBrand) {
      toast({
        title: 'Missing information',
        description: 'Please select a brand from the suggestions and upload a file',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Show preview
    const tempFileUrl = URL.createObjectURL(uploadedFile);

    setModalState({
      type: 'preview',
      file: uploadedFile,
      contentUrl: tempFileUrl,
      brand: selectedBrand.name,
    });
    setIsSubmitting(false); // Reset loading state when showing preview
  };

  const handleConfirmSubmission = async () => {
    if (modalState.type !== 'preview') return;

    const { file, brand } = modalState;

    setIsSubmitting(true);

    try {
      const contentType: 'video' | 'image' = file.type.startsWith('video/') ? 'video' : 'image';
      const mimeType = file.type;
      const fileExtension = file.name.split('.').pop() || (contentType === 'video' ? 'mp4' : 'jpg');

      // Create portfolio record (pending) and receive the signed upload URL in one call
      const { portfolioId, uploadUrl } = await contentPortfolioApi.createPortfolioContent({
        brand,
        postLink: '',
        contentType,
        fileExtension,
        mimeType,
      }, influencerData.id);

      // Upload to Firebase Storage
      await uploadWithProgress(uploadUrl!, file, () => {});

      // Confirm the upload so the record becomes visible
      await contentPortfolioApi.confirmUpload(portfolioId, influencerData.id);

      // Clean up blob URL
      URL.revokeObjectURL(modalState.contentUrl);

      // Success!
      toast({
        title: 'Content added to library',
        description: 'Your content has been successfully added to your portfolio',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (onSuccess) {
        onSuccess();
      }

      handleClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your content. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false); // Re-enable the button on error
    }
  };

  const handleRetry = () => {
    // Clean up blob URL if exists
    if (modalState.type === 'preview') {
      URL.revokeObjectURL(modalState.contentUrl);
    }
    setModalState({ type: 'input' });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const isSubmitEnabled =
    uploadMode === 'marketplaceTop'
      ? (uploadedFile !== null || isEditingExisting) && resolvedBrandName.length >= 2 && !isSubmitting
      : uploadedFile !== null && isBrandSelected && !isSubmitting;

  // Show preview modal if in preview state
  if (modalState.type === 'preview') {
    const previewContentType: 'video' | 'image' = modalState.file.type.startsWith('video/') ? 'video' : 'image';
    return (
      <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
        <ModalOverlay />
        <ReviewContentModalConfirmation
          isOpen={isOpen}
          onClose={handleClose}
          contentUrl={modalState.contentUrl}
          contentType={previewContentType}
          handleSubmitStory={handleConfirmSubmission}
          handleRetry={handleRetry}
          isLoading={isSubmitting}
        />
      </Modal>
    );
  }

  // Bottom bar over a media preview (Replace / Remove).
  const mediaBarProps = {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    minH: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bg: 'rgba(14, 14, 14, 0.72)',
    cursor: 'pointer',
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={handleClose}
      title={titleText}
      footer={
        // Sticky so Submit stays reachable under a tall preview; the dialog container is the scroller.
        <Flex
          position="sticky"
          bottom={0}
          gap={3}
          mt={5}
          mx={-5}
          px={5}
          pt="14px"
          pb="env(safe-area-inset-bottom)"
          bg="white"
          borderTop={`1px solid ${ff.line}`}
        >
          <Button variant="inkOutline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="lime"
            flex={1}
            onClick={handleSubmit}
            isDisabled={!isSubmitEnabled}
            isLoading={isSubmitting}
          >
            Submit
          </Button>
        </Flex>
      }
    >
      <VStack spacing={5} align="stretch">
        <FormControl position="relative">
          <FormLabel>Brand name</FormLabel>
          <Flex align="center" position="relative">
            {selectedBrand && selectedBrand.icon && (
              <Box position="absolute" left={3} zIndex={2}>
                <Image src={selectedBrand.icon} alt={selectedBrand.name} boxSize="24px" borderRadius="6px" objectFit="contain" />
              </Box>
            )}
            <Input
              value={brandName}
              onChange={handleBrandNameChange}
              placeholder="Start typing brand name..."
              borderColor={isBrandSelected ? ff.ink : undefined}
              pl={selectedBrand && selectedBrand.icon ? '44px' : '16px'}
              fontSize="16px"
            />
          </Flex>

          {showSuggestions && suggestions.length > 0 && (
            <List
              position="absolute"
              top="100%"
              left={0}
              right={0}
              bg="white"
              boxShadow="0 8px 24px rgba(14, 14, 14, 0.08)"
              borderRadius="14px"
              maxH="200px"
              overflowY="auto"
              zIndex={1000}
              mt={2}
              border="1px solid"
              borderColor={ff.line}
            >
              {suggestions.map((suggestion, index) => (
                <ListItem
                  key={index}
                  minH="52px"
                  px={3}
                  py={2}
                  cursor="pointer"
                  _hover={{ bg: ff.grey }}
                  borderTop={index === 0 ? 'none' : '1px solid'}
                  borderColor={ff.line}
                  onClick={() => handleSuggestionClick(suggestion)}
                  display="flex"
                  alignItems="center"
                  gap={3}
                >
                  {suggestion.icon && (
                    <Image
                      src={suggestion.icon}
                      alt={suggestion.name}
                      boxSize="32px"
                      borderRadius="8px"
                      objectFit="contain"
                      flexShrink={0}
                    />
                  )}
                  <Text fontWeight={500} fontSize="15px" color={ff.ink}>
                    {suggestion.name}
                  </Text>
                </ListItem>
              ))}
            </List>
          )}
        </FormControl>

        <Box>
          <Text fontSize="14px" fontWeight={600} color={ff.ink} mb="6px">
            File
          </Text>

          {!uploadedFile && isEditingExisting ? (
            <Box display="flex" justifyContent="center" w="100%">
              <Box borderRadius="20px" overflow="hidden" position="relative" maxW="100%" lineHeight={0}>
                {initialIsVideo ? (
                  <Box
                    as="video"
                    src={initialContentUrl}
                    muted
                    loop
                    playsInline
                    autoPlay
                    display="block"
                    maxW="100%"
                    maxH="50vh"
                    w="auto"
                    h="auto"
                  />
                ) : (
                  <Image src={initialContentUrl} alt="Current content" display="block" maxW="100%" maxH="50vh" w="auto" h="auto" />
                )}
                <Box {...getRootProps()} {...mediaBarProps}>
                  <input {...getInputProps()} />
                  <Text fontFamily="display" fontSize="14px" fontWeight={600} color="white" lineHeight="1">
                    Replace
                  </Text>
                </Box>
              </Box>
            </Box>
          ) : !uploadedFile ? (
            <Box
              {...getRootProps()}
              border="1.5px dashed"
              borderColor={isDragActive ? ff.ink : ff.border}
              borderRadius="20px"
              p={8}
              textAlign="center"
              bg={isDragActive ? ff.limeSurface : ff.grey}
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ borderColor: ff.ink }}
            >
              <input {...getInputProps()} />
              <VStack spacing={4}>
                <Flex w="56px" h="56px" align="center" justify="center" bg={ff.lime} borderRadius="full">
                  <Icon as={FaUpload} boxSize={5} color={ff.ink} />
                </Flex>
                <Box>
                  <Text fontFamily="display" fontWeight={600} fontSize="16px" mb={1} color={ff.ink}>
                    {isDragActive ? 'Drop file here' : 'Drag and drop or click to browse'}
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    Images: any format
                  </Text>
                  <Text fontSize="13px" color={ff.text2}>
                    Videos: MP4, MOV, or WebM only
                  </Text>
                </Box>
              </VStack>
            </Box>
          ) : uploadMode === 'marketplaceTop' ? (
            <Box display="flex" justifyContent="center" w="100%">
              <Box borderRadius="20px" overflow="hidden" position="relative" maxW="100%" lineHeight={0}>
                {newFilePreviewUrl &&
                  (uploadedFile.type.startsWith('video/') ? (
                    <Box
                      as="video"
                      src={newFilePreviewUrl}
                      muted
                      loop
                      playsInline
                      autoPlay
                      display="block"
                      maxW="100%"
                      maxH="50vh"
                      w="auto"
                      h="auto"
                    />
                  ) : (
                    <Image src={newFilePreviewUrl} alt="Preview" display="block" maxW="100%" maxH="50vh" w="auto" h="auto" />
                  ))}
                <Box {...mediaBarProps} onClick={() => setUploadedFile(null)}>
                  <Text fontFamily="display" fontSize="14px" fontWeight={600} color="white" lineHeight="1">
                    Remove
                  </Text>
                </Box>
              </Box>
            </Box>
          ) : (
            <Flex align="center" justify="space-between" border="1px solid" borderColor={ff.line} borderRadius="20px" p={4} bg="white">
              <Flex align="center" flex={1} minW={0}>
                <Flex w="40px" h="40px" align="center" justify="center" bg={ff.lime} borderRadius="full" mr={3} flexShrink={0}>
                  <Icon as={FaCheckCircle} boxSize={4} color={ff.ink} />
                </Flex>
                <Box flex={1} minW={0}>
                  <Text fontWeight={500} fontSize="14px" color={ff.ink} noOfLines={1}>
                    {uploadedFile.name}
                  </Text>
                  <Text fontSize="12px" color={ff.text2}>
                    {formatFileSize(uploadedFile.size)}
                  </Text>
                </Box>
              </Flex>
              <Button
                variant="ghost"
                h="44px"
                color={ff.danger}
                fontWeight={600}
                onClick={() => setUploadedFile(null)}
                ml={2}
              >
                Remove
              </Button>
            </Flex>
          )}
        </Box>

        {uploadMode === 'marketplaceTop' && (
          <FormControl>
            <FormLabel>
              Caption{' '}
              <Text as="span" fontWeight={400} color={ff.muted}>
                (optional)
              </Text>
            </FormLabel>
            <Textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, 100))}
              placeholder="e.g. Unboxing haul for summer collection..."
              fontSize="16px"
              resize="none"
              rows={2}
            />
            <Text fontSize="12px" color={ff.muted} mt={1} textAlign="right">
              {caption.length}/100
            </Text>
          </FormControl>
        )}
      </VStack>
    </ModalShell>
  );
};

export default UploadContentModal;
